import React, { createContext, useContext, useState, ReactNode, useEffect, useCallback } from 'react';
import apiClient from '../api/axiosConfig';
import axios from 'axios';

// Create an event bus for session expiration
export const sessionEvents = {
  onSessionExpired: () => {
    const event = new CustomEvent('session-expired');
    window.dispatchEvent(event);
  }
};

interface AuthContextType {
  isAuthenticated: boolean;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
  error: string | null;
  sessionExpired: boolean;
  clearSessionExpired: () => void;
}

const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  login: async () => false,
  logout: () => {},
  error: null,
  sessionExpired: false,
  clearSessionExpired: () => {},
});

export const useAuth = () => useContext(AuthContext);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  // Initialize isAuthenticated based on token existence
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => {
    let token = localStorage.getItem('token');
    return !!token;
  });
  const [error, setError] = useState<string | null>(null);
  const [sessionExpired, setSessionExpired] = useState<boolean>(false);

  const clearSessionExpired = () => {
    setSessionExpired(false);
  };

  // No need to set Authorization header here as it's handled by the apiClient interceptor

  const login = async (username: string, password: string): Promise<boolean> => {
    try {
      setError(null);
      const response = await apiClient.post('/api/auth/login', { username, password });
      const { token } = response.data;

      // Save token to localStorage
      localStorage.setItem('token', token);

      setIsAuthenticated(true);
      return true;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        setError(error.response.data.error || 'Login failed');
      } else {
        setError('An unexpected error occurred');
      }
      return false;
    }
  };

  const logout = useCallback(() => {
    // Remove token from localStorage
    localStorage.removeItem('token');

    setIsAuthenticated(false);
  }, [setIsAuthenticated]);

  // Add event listener for session expiration
  useEffect(() => {
    const handleSessionExpired = () => {
      setSessionExpired(true);
      logout();
    };

    window.addEventListener('session-expired', handleSessionExpired);

    return () => {
      window.removeEventListener('session-expired', handleSessionExpired);
    };
  }, [logout]);

  const value = {
    isAuthenticated,
    login,
    logout,
    error,
    sessionExpired,
    clearSessionExpired,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
